import React, { useRef, useMemo, useCallback, useEffect, useState } from 'react'
import { useField, useFormikContext } from 'formik'

import NumberBox, { NumberBoxRef } from 'devextreme-react/number-box'
import { useSelector } from 'react-redux'
import { RootState } from 'slices'
import { useLabelWidth } from 'hooks/use-label-width'
import { FormErrors } from 'components/forms/form-errors'
import useResizeObserver from '@react-hook/resize-observer'

import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'

import './index.scss'


export const NumberInput = ({label, readOnly=false, min, max, format, ...props}: any) => {
    const { setFieldValue } = useFormikContext()
    const [field, meta] = useField(props)
    const inputRef = useRef<NumberBoxRef>(null)
    const [fieldWidth, setFieldWidth] = useState<any>(null)
    const [inFocus, setInFocus] = useState(false)
    const {value: formFieldType} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.formFieldType))
    const getLabelWidth = useLabelWidth(formFieldType)
    const isFloatingLabel = useMemo(() => {
        return formFieldType === 'floating-label' || formFieldType === 'floating-label-compact'
    },[formFieldType])

    const onResize = useCallback((e: any) => {
        isFloatingLabel && setFieldWidth(e.contentRect.width)
    },[isFloatingLabel])

    useResizeObserver((inputRef.current?.instance()?.element().querySelector('.dx-texteditor-input-container') as any), onResize)

    useEffect(() => {
        if (isFloatingLabel) {
            const numberBox: any = inputRef.current
            const floatingLabel: HTMLElement = numberBox?.instance()?.element().querySelector('.dx-label')

            if (floatingLabel) {
                const fontFamily = window.getComputedStyle(floatingLabel).fontFamily
                const floatingLabelWidth = getLabelWidth(label, fontFamily)
                const input: any = numberBox?.instance()?.element().querySelector('input.dx-texteditor-input')

                fieldWidth && (input.style.width = `${fieldWidth - (floatingLabelWidth+18)}px`)
            }

            if (field.value || field.value === 0) {
                numberBox?.instance()?.element().classList.add('input-has-value')
                floatingLabel && (floatingLabel.style.left = `calc(100% - ${floatingLabel.offsetWidth}px)`)
            }

            else {
                numberBox?.instance()?.element().classList.remove('input-has-value')
                floatingLabel && (floatingLabel.style.left = inFocus ? `calc(100% - ${floatingLabel.offsetWidth}px)` : '0px')

            }
        }

    },[field.value, getLabelWidth, label, fieldWidth, inFocus, isFloatingLabel])

    const handleChange = (e: any) => {
        setFieldValue(field.name, e.value === undefined ? null : e.value, true)
    }

    const handleBlur = (e: any) => {
        field.onBlur(e.event)
        setInFocus(false)
    }

    const onFocusIn = useCallback(() => {
        setInFocus(true)
    }, [])

    const ddButtons = useMemo<any>(() => ([
        {
            location: 'before',
            name: 'label',
            options: {
                text: label,
                stylingMode: 'text',
                hoverStateEnabled: false,
                focusStateEnabled: false,
                activeStateEnabled: false,
                elementAttr: {
                    class: 'label-inside-field'
                }
            }

        }
    ]), [label])

    return (
        <div className='rb-item mb-1'>
            <div>
                <label htmlFor={props.id || props.name} className='dx-field-label'>{label}</label>
                <div className='dx-field-value'>
                    <NumberBox
                        ref={inputRef}
                        label={label}
                        labelMode= 'floating'
                        buttons={ddButtons}
                        name={field.name}
                        inputAttr={{id: field.name, name: field.name}}
                        value={field.value !== null && field.value !== undefined ? parseFloat(field.value) : undefined}
                        onValueChanged={handleChange}
                        onFocusOut={handleBlur}
                        onFocusIn={onFocusIn}
                        className='dx-field-input'
                        readOnly={readOnly}
                        isValid={!(meta.touched && meta.error)}
                        min={min}
                        max={max}
                        format={format}
                        height={26}
                    />
                </div>
            </div>
            <FormErrors meta={meta} />
        </div>
    )
}
